/*
  V45.8.310 — HOMEPAGE ACCESSORIES + TOYS JP → EN SCRAMBLE

  Scope:
  - Every product card inside the homepage Accessories and Toys secondary surfaces.
  - Product title: Japanese hold → mixed JP/Latin scramble → original English title.
  - Price/status: compact symbol/alphanumeric scramble → original price/status.

  Lifecycle / performance:
  - No vertical entrance motion.
  - No scroll listener, interval, or continuous RAF loop.
  - One shared IntersectionObserver watches only live cards.
  - Theme Editor section load/unload is supported and old cards are unobserved/cleaned.
  - ScrambleTextPlugin is lazy-loaded only on the first real viewport entry.
*/
(() => {
  'use strict';

  const RUNTIME_KEY = '__u20SecondaryProductScrambleV458310';
  const LEGACY_RUNTIME_KEY = '__u20SecondaryProductScrambleV458216';
  if (window[RUNTIME_KEY]) {
    window[RUNTIME_KEY].initWithin(document);
    return;
  }

  const CARD_SELECTOR = '[data-u20-secondary-surface] [data-product-grid-item]';
  const SCRAMBLE_PLUGIN_SRC = window.__u20ScrambleTextPluginSrc || '';
  const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Current homepage Accessories + Toys translations.
  // Keep UnDeR20 fixed as アンダートゥエンティー whenever it appears in Japanese.
  const JAPANESE_TITLES = Object.freeze({
    'Local Cap': 'ローカルキャップ',
    'Monster Texture Metal-Shaped Bucket Hat': 'モンスターテクスチャー メタルシェイプ バケットハット',
    'Tokyo Spine T5 Axis Ring': '東京スパイン T5 アクシスリング',
    'Wooden Toy Blueprint Tote Bag': '木製玩具設計図 トートバッグ',
    'FUCK YOU PAY ME Credit Card Keychain': '「FUCK YOU PAY ME」クレジットカード キーチェーン',
    'UnDeR20 × LINWENXIN Lover of the Dead': 'アンダートゥエンティー × LINWENXIN ラバー・オブ・ザ・デッド',
    'Green Monster Skateboard Deck': 'グリーンモンスター スケートボードデッキ',
    'Instant Meal': 'インスタントミール',

    'TenGuSan Wooden Automata Toy': 'TenGuSan 木製オートマタトイ',
    'TenGuSan St. Sebastian Art toy': 'TenGuSan 聖セバスティアヌス アートトイ',
    'TenGuSan St. Sebastia Reaction Figure': 'TenGuSan 聖セバスティア リアクションフィギュア',
    'Crawling Shisuka SOFUBI Toy 2024': 'クロウリング・シスカ ソフビトイ 2024',
    'TenGuSan Finger Puppet': 'TenGuSan フィンガーパペット',
    'TenGuDaruma': '天狗だるま',
    'Crawling Shisuka SOFUBI Toy': 'クロウリング・シスカ ソフビトイ',
    'Lola In Multiverse - MANTA Ver.': 'ローラ・イン・マルチバース — MANTA Ver.',
    'Lola In Multiverse - Corpse Ver.': 'ローラ・イン・マルチバース — コープス Ver.',
    'Lola In Multiverse - Marilyn Yayoi Ver.': 'ローラ・イン・マルチバース — マリリン・ヤヨイ Ver.',
    'Lola In Multiverse - First Ver.': 'ローラ・イン・マルチバース — ファースト Ver.',
    'TenGuSan Coverall Art Toy': 'TenGuSan カバーオール アートトイ'
  });

  const TITLE_CHARS = 'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモラリルレロワンABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz01!<>-_\\/[]{}—=+*^?#';
  const PRICE_CHARS = '0123456789$¥€£ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz!<>-_+=?#';

  // V45.8.296 — slightly slower visual cadence:
  // let the Japanese state register, then give the scramble/reveal enough time
  // to be consciously readable without turning the section into a slow intro.
  const JAPANESE_HOLD = 0.28;
  const TITLE_DURATION = 1.05;
  const PRICE_DELAY = 0.30;
  const PRICE_DURATION = 0.78;
  const ENTER_RATIO = 0.20;
  const EXIT_RATIO = 0.01;
  const PLUGIN_SETTLE_TIMEOUT = 3500;
  const PLUGIN_RETRY_DELAY = 300;
  const PLUGIN_MAX_ATTEMPTS = 2;

  const observedCards = new Set();
  const ignoredPluginScripts = new WeakSet();
  let observer = null;
  let pluginPromise = null;
  let pluginAttempts = 0;

  const normalizeText = (value) => (value || '').replace(/\s+/g, ' ').trim();

  const getJapaneseTitle = (englishTitle) => {
    const exact = JAPANESE_TITLES[englishTitle];
    if (exact) return exact;
    if (/UnDeR20/i.test(englishTitle)) {
      return englishTitle.replace(/UnDeR20/gi, 'アンダートゥエンティー');
    }
    return englishTitle;
  };

  const settlePluginScript = (script, owned, resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
      if (!value) {
        ignoredPluginScripts.add(script);
        if (owned && script.parentNode) script.remove();
      }
      resolve(value);
    };
    const onLoad = () => finish(!!window.ScrambleTextPlugin);
    const onError = () => finish(false);
    const timer = window.setTimeout(() => finish(!!window.ScrambleTextPlugin), PLUGIN_SETTLE_TIMEOUT);

    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener('error', onError, { once: true });
    // A prior copy may have completed before this runtime attached listeners.
    queueMicrotask(() => {
      if (window.ScrambleTextPlugin) finish(true);
      else if (script.dataset.u20SecondaryScrambleSettled === 'error' || script.dataset.u20SecondaryScrambleSettled === 'loaded') finish(false);
    });
  };

  const loadScramblePluginOnce = (attempt) => new Promise((resolve) => {
    const existing = Array.from(document.scripts).find((script) =>
      !ignoredPluginScripts.has(script) &&
      (script.src === SCRAMBLE_PLUGIN_SRC || script.src.includes('ScrambleTextPlugin.min.js'))
    );
    if (existing) {
      settlePluginScript(existing, false, resolve);
      return;
    }

    const script = document.createElement('script');
    script.src = attempt > 1
      ? `${SCRAMBLE_PLUGIN_SRC}?u20retry=${attempt}`
      : SCRAMBLE_PLUGIN_SRC;
    script.async = true;
    script.dataset.u20SecondaryScramblePlugin = 'true';
    script.dataset.u20SecondaryScrambleAttempt = String(attempt);
    script.addEventListener('load', () => {
      script.dataset.u20SecondaryScrambleSettled = 'loaded';
    }, { once: true });
    script.addEventListener('error', () => {
      script.dataset.u20SecondaryScrambleSettled = 'error';
    }, { once: true });
    settlePluginScript(script, true, resolve);
    document.head.appendChild(script);
  });

  const ensureScramblePlugin = () => {
    if (window.ScrambleTextPlugin) return Promise.resolve(true);
    if (!window.gsap || !SCRAMBLE_PLUGIN_SRC) return Promise.resolve(false);
    if (pluginPromise) return pluginPromise;

    pluginPromise = (async () => {
      while (!window.ScrambleTextPlugin && pluginAttempts < PLUGIN_MAX_ATTEMPTS) {
        pluginAttempts += 1;
        if (await loadScramblePluginOnce(pluginAttempts)) return true;
        if (pluginAttempts < PLUGIN_MAX_ATTEMPTS) {
          await new Promise((resolve) => window.setTimeout(resolve, PLUGIN_RETRY_DELAY));
        }
      }
      return !!window.ScrambleTextPlugin;
    })().finally(() => {
      if (!window.ScrambleTextPlugin) pluginPromise = null;
    });

    return pluginPromise;
  };

  const getHosts = (card) => ({
    titleHost: card?.querySelector('.product-item__title') || null,
    priceHosts: card ? Array.from(card.querySelectorAll('.product-item__price .new-price, .product-item__price .old-price, .product-item__price .sold-out')) : []
  });

  const snapshotCard = (card) => {
    if (!card || card._u20SecondaryScrambleSnapshot) return card?._u20SecondaryScrambleSnapshot || null;
    const { titleHost, priceHosts } = getHosts(card);
    if (!titleHost) return null;

    const titleText = normalizeText(titleHost.textContent);
    const snapshot = {
      titleHost,
      titleText,
      japaneseTitle: getJapaneseTitle(titleText),
      priceHosts: priceHosts.map((host) => ({
        host,
        text: normalizeText(host.textContent),
        html: host.innerHTML
      }))
    };
    card._u20SecondaryScrambleSnapshot = snapshot;
    return snapshot;
  };

  const removeVisualOverlay = (host) => {
    if (!host) return;
    host.querySelectorAll(':scope > .u20-secondary-scramble-overlay').forEach((node) => node.remove());
  };

  const restorePlainText = (card) => {
    const snapshot = card?._u20SecondaryScrambleSnapshot;
    if (!snapshot) return;

    if (snapshot.titleHost?.isConnected) {
      removeVisualOverlay(snapshot.titleHost);
      /* V45.8.310 — the real layout text is never replaced during animation.
         It remains the immutable geometry source and only gets hidden visually. */
      snapshot.titleHost.style.removeProperty('visibility');
      snapshot.titleHost.style.removeProperty('position');
      snapshot.titleHost.style.removeProperty('min-height');
    }

    snapshot.priceHosts.forEach(({ host }) => {
      if (!host?.isConnected) return;
      removeVisualOverlay(host);
      host.style.removeProperty('visibility');
      host.style.removeProperty('position');
      host.style.removeProperty('min-height');
    });
  };

  const killTimeline = (card) => {
    const timeline = card?._u20SecondaryScrambleTimeline;
    if (!timeline) return;
    try { timeline.kill(); } catch (_) {}
    card._u20SecondaryScrambleTimeline = null;
  };

  const createVisualOverlay = (host, initialText) => {
    if (!host?.isConnected) return null;

    removeVisualOverlay(host);

    /* Freeze the host's current English geometry before hiding it. This does
       not change width/height; it only ensures the absolutely positioned
       visual copy has a stable containing block. */
    const rect = host.getBoundingClientRect();
    host.style.position = 'relative';
    if (rect.height > 0) host.style.minHeight = `${rect.height}px`;

    const overlay = document.createElement('span');
    overlay.className = 'u20-secondary-scramble-overlay';
    overlay.textContent = initialText || '';
    overlay.setAttribute('aria-hidden', 'true');

    Object.assign(overlay.style, {
      position: 'absolute',
      inset: '0',
      display: 'block',
      width: '100%',
      height: '100%',
      visibility: 'visible',
      pointerEvents: 'none',
      color: 'inherit',
      WebkitTextFillColor: 'currentColor',
      overflow: 'visible'
    });

    host.appendChild(overlay);

    /* visibility:hidden preserves the original host's exact layout box.
       The child explicitly re-enables visibility, so only the overlay paints. */
    host.style.visibility = 'hidden';
    overlay.style.visibility = 'visible';

    return overlay;
  };

  const resetCardState = (card, dropSnapshot = false) => {
    if (!card) return;
    killTimeline(card);
    restorePlainText(card);
    card.dataset.u20SecondaryScrambleInView = 'false';
    card.dataset.u20SecondaryScrambleActive = 'false';
    card.dataset.u20SecondaryScrambleArmed = 'true';
    if (dropSnapshot) delete card._u20SecondaryScrambleSnapshot;
  };

  const animateCard = async (card) => {
    if (!card || !observedCards.has(card) || card.dataset.u20SecondaryScrambleActive === 'true') return;
    if (REDUCED_MOTION_QUERY.matches) {
      resetCardState(card, false);
      return;
    }
    card.dataset.u20SecondaryScrambleActive = 'true';

    const snapshot = snapshotCard(card);
    if (!snapshot || !snapshot.titleText) {
      card.dataset.u20SecondaryScrambleActive = 'false';
      return;
    }

    killTimeline(card);

    /* V45.8.310 — animate visual overlays, never the real title/price nodes.
       The real English text remains untouched in normal document flow, so
       Japanese length / scramble glyph width cannot change card height. */
    const titleOverlay = createVisualOverlay(
      snapshot.titleHost,
      snapshot.japaneseTitle
    );

    const priceOverlays = snapshot.priceHosts.map(({ host, text }) => ({
      host,
      text,
      overlay: createVisualOverlay(host, text)
    }));

    if (!titleOverlay) {
      restorePlainText(card);
      card.dataset.u20SecondaryScrambleActive = 'false';
      return;
    }

    const pluginReady = await ensureScramblePlugin();
    if (!card.isConnected || !observedCards.has(card) || card.dataset.u20SecondaryScrambleInView !== 'true') {
      restorePlainText(card);
      card.dataset.u20SecondaryScrambleActive = 'false';
      return;
    }

    const gsap = window.gsap || null;
    if (!gsap || !pluginReady || !window.ScrambleTextPlugin) {
      restorePlainText(card);
      card.dataset.u20SecondaryScrambleActive = 'false';
      return;
    }

    try {
      gsap.registerPlugin(window.ScrambleTextPlugin);

      const timeline = gsap.timeline({
        onComplete: () => {
          restorePlainText(card);
          card._u20SecondaryScrambleTimeline = null;
          card.dataset.u20SecondaryScrambleActive = 'false';
        }
      });
      card._u20SecondaryScrambleTimeline = timeline;

      timeline.to(titleOverlay, {
        duration: TITLE_DURATION,
        scrambleText: {
          text: snapshot.titleText,
          chars: TITLE_CHARS,
          revealDelay: 0.14,
          speed: 0.58
        }
      }, JAPANESE_HOLD);

      priceOverlays.forEach(({ overlay, text }, index) => {
        if (!overlay || !text) return;
        timeline.to(overlay, {
          duration: PRICE_DURATION,
          scrambleText: {
            text,
            chars: PRICE_CHARS,
            revealDelay: 0.11,
            speed: 0.62
          }
        }, PRICE_DELAY + index * 0.03);
      });
    } catch (error) {
      console.warn('[U20 JP→EN scramble] animation failed; restoring product text.', error);
      resetCardState(card, false);
    }
  };

  const getObserver = () => {
    if (observer || !('IntersectionObserver' in window)) return observer;
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const card = entry.target;
        if (!observedCards.has(card)) return;
        const ratio = entry.intersectionRatio || 0;

        if (entry.isIntersecting && ratio >= ENTER_RATIO) {
          card.dataset.u20SecondaryScrambleInView = 'true';
          if (card.dataset.u20SecondaryScrambleArmed === 'true') {
            card.dataset.u20SecondaryScrambleArmed = 'false';
            animateCard(card);
          }
          return;
        }

        if (!entry.isIntersecting || ratio <= EXIT_RATIO) {
          resetCardState(card, false);
        }
      });
    }, {
      root: null,
      threshold: [0, EXIT_RATIO, ENTER_RATIO, 0.35]
    });
    return observer;
  };

  const collectCards = (root) => {
    if (!root) return [];
    const cards = [];
    if (root.matches?.(CARD_SELECTOR)) cards.push(root);
    root.querySelectorAll?.(CARD_SELECTOR).forEach((card) => cards.push(card));
    return cards;
  };

  const initWithin = (root) => {
    const cards = collectCards(root);
    if (!cards.length) return;
    const liveObserver = getObserver();

    cards.forEach((card) => {
      if (observedCards.has(card)) return;
      snapshotCard(card);
      card.dataset.u20SecondaryScrambleInView = 'false';
      card.dataset.u20SecondaryScrambleActive = 'false';
      card.dataset.u20SecondaryScrambleArmed = 'true';
      observedCards.add(card);
      liveObserver?.observe(card);
    });
  };

  const destroyWithin = (root) => {
    collectCards(root).forEach((card) => {
      if (!observedCards.has(card)) return;
      observer?.unobserve(card);
      observedCards.delete(card);
      resetCardState(card, true);
    });
  };

  const destroy = () => {
    Array.from(observedCards).forEach((card) => {
      observer?.unobserve(card);
      resetCardState(card, true);
    });
    observedCards.clear();
    observer?.disconnect();
    observer = null;
    if (typeof REDUCED_MOTION_QUERY.removeEventListener === 'function') {
      REDUCED_MOTION_QUERY.removeEventListener('change', onReducedMotionChange);
    } else if (typeof REDUCED_MOTION_QUERY.removeListener === 'function') {
      REDUCED_MOTION_QUERY.removeListener(onReducedMotionChange);
    }
  };

  const onReducedMotionChange = () => {
    observedCards.forEach((card) => {
      resetCardState(card, false);
      observer?.unobserve(card);
      if (!REDUCED_MOTION_QUERY.matches) observer?.observe(card);
    });
  };

  const runtime = { initWithin, destroyWithin, destroy };
  window[RUNTIME_KEY] = runtime;
  // Replace the old boolean marker with the lifecycle-aware runtime so theme
  // editor reloads cannot mistake the legacy implementation for this one.
  window[LEGACY_RUNTIME_KEY] = runtime;

  const init = () => runtime.initWithin(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => runtime.initWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => runtime.destroyWithin(event.target));
  if (typeof REDUCED_MOTION_QUERY.addEventListener === 'function') {
    REDUCED_MOTION_QUERY.addEventListener('change', onReducedMotionChange);
  } else if (typeof REDUCED_MOTION_QUERY.addListener === 'function') {
    REDUCED_MOTION_QUERY.addListener(onReducedMotionChange);
  }
})();
